'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useState, useEffect, useRef, useMemo } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { showNotification } from '@mantine/notifications';

// UI lib components
import { Stack, Container, Group, Button, Box } from '@mantine/core';

// UI local components
import ConfirmBar from '@ui-kit/molecules/ConfirmBar';

// Context
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';
import useAccount from '@features/Account/store/useAccount';

// Store
import dialogStoreVar from '@features/Dialog/store/dialogStore';

// API & API types
import {
  useBulkMoveResourcesMutation,
  Resource,
  ResourceType,
  useGetOneResourceQuery,
} from '@app-types/blackroom-api';
import { ResourceScope } from '@app-types/blackroom-app';
import { DialogResourceMoveProps } from './types';

// Style
import * as classes from './styles/index.css';
import { palette } from '@app/shared/ui-kit/colors';
import ResourceTable from '../ResourceTable';
import ResourcePath from '../ResourcePath';
import { PAGE_SIZE } from '../ResourceTable/types';
import { MRT_SortingState } from 'mantine-react-table';
import LIST_RESOURCES from '../api/listResources';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

/**
 * A self-managed dialog for editing/creating new files/folders
 */
function DialogResourceMove({
  dialogResources,
  resetRowSelection,
  requestVariables,
}: DialogResourceMoveProps) {
  /* ********************************** HOOKS ********************************* */

  // Ref
  const focusRef = useRef<HTMLInputElement>(null);

  // State
  const [targetFolder, setTargetFolder] = useState<
    Pick<Resource, 'id' | 'type'> | undefined
  >();
  const [sorting, setSorting] = useState<MRT_SortingState>([]);

  // Context
  const workspace = useWorkspace();
  const workspaceId = workspace.id;
  const { isWorkspaceAdmin } = useAccount();

  // Store
  const dialogStore = useReactiveVar(dialogStoreVar);
  const { dialogIdentifier, closeDialog } = dialogStore ?? {};

  // Routing
  const { scope = 'my', folderId = null } = useParams<{
    scope: ResourceScope;
    folderId?: string;
  }>();

  // API
  const [moveResources, { loading: loadingMoveResources }] =
    useBulkMoveResourcesMutation();
  const { data: parentData } = useGetOneResourceQuery({
    variables: { workspaceId: workspace.id, resourceId: folderId as string },
    skip: !folderId,
    fetchPolicy: 'cache-and-network',
  });

  // Locale
  const { t } = useTranslation();

  // Side-effects
  useEffect(
    function onMount() {
      if (dialogIdentifier === 'dialog-resource-edit') {
        focusRef.current?.focus();
      }
    },
    [dialogIdentifier],
  );

  const parent = parentData?.resource as Resource | undefined;

  /* ***************************** LOCAL VARIABLES **************************** */

  const resourcesToMove = dialogResources ?? [];
  const resourcesToMoveIds = resourcesToMove.map((r) => r.id);

  const isAtRoot = !folderId;

  const isTargetFolderSelectable = useMemo(
    () =>
      (targetFolder &&
        targetFolder.type === ResourceType.Folder &&
        !resourcesToMoveIds.includes(targetFolder.id as string)) ||
      // Allow "move here" (current folder) only if not at root for non-admins
      (!targetFolder && !(!isWorkspaceAdmin(workspace) && isAtRoot)),
    [resourcesToMoveIds, targetFolder, isAtRoot, isWorkspaceAdmin, workspace],
  );

  /* ******************************** CALLBACKS ******************************* */

  async function onSubmit({ placeOnRoot = false }: { placeOnRoot?: boolean }) {
    // Check if target folder has children of the same name (this includes workspace root)
    const parentId = placeOnRoot
      ? null
      : ((targetFolder as Resource | undefined)?.id ?? parent?.id ?? null);

    // Update resource with target folder as parent and optionally with a new name
    // with an index following latest duplicate index
    await moveResources({
      variables: {
        resourcesToMove: resourcesToMoveIds,
        to: parentId,
      },
      update(cache) {
        resourcesToMoveIds.forEach((id) => {
          cache.evict({
            id: cache.identify({ __typename: 'Resource', id }),
            fieldName: 'path',
          });
        });
        cache.gc();
      },
      refetchQueries: [
        // Query of the page where was initiated the move operation
        {
          query: LIST_RESOURCES,
          variables: requestVariables,
        },
        {
          // Query of the target folder
          query: LIST_RESOURCES,
          variables: {
            ...requestVariables,
            filter: { ...requestVariables?.filter, parentId },
          },
        },
      ],
    })
      .catch((error) => {
        showNotification({
          title: t('Common:errors.genericTitle'),
          message: t('Common:errors.genericMessage'),
          color: 'blackroomRed',
        });
        console.error(error);
      })
      .finally(() => {
        resetRowSelection?.();
        closeDialog?.('dialog-resource-move');
      });
  }

  /* ******************************** RENDERING ******************************* */

  return (
    <Container className={classes.dialogResourceEdit} fluid>
      <Stack align="stretch" gap={0} h="100%" pb="md">
        <ResourcePath
          parentId={parent?.id}
          scope={scope}
          navigation="page"
          withRootElement
          showRootElementAtRoot
          disableRootNavigation={!isWorkspaceAdmin(workspace)}
        />
        <ResourceTable
          displayColumnDefOptions={{
            'mrt-row-expand': {
              size: 10,
              maxSize: 10,
            },
          }}
          scope={scope}
          columns={{
            index: true,
            name: true,
            createdBy: true,
            createdAt: true,
          }}
          requestVariables={{
            filter: {
              workspaceId,
              type: ResourceType.Folder,
              parentId: folderId,
            },
            limit: PAGE_SIZE,
            offset: 0,
          }}
          onSelectResources={(sr) => setTargetFolder(sr[0])}
          enableRowSelection
          enableMultiRowSelection={false}
          enableExpanding={false}
          withNavigation
          selectedResources={[]}
          sorting={sorting}
          onSortingChange={setSorting}
        />

        <Group justify="space-between">
          {isWorkspaceAdmin(workspace) && (
            <Button onClick={() => onSubmit({ placeOnRoot: true })}>
              {t('Resource:DialogResourceMove.ctaMoveToWorkspaceRoot', {
                workspaceName: workspace.name,
              })}
            </Button>
          )}
          {targetFolder && !isTargetFolderSelectable && (
            <Box c={palette.blackroomRed} fw="bold" fz="sm" ta="center">
              {t('Resource:DialogResourceMove.cannotMoveToItself')}
            </Box>
          )}
          <ConfirmBar
            onConfirm={() => {
              onSubmit({});
            }}
            onCancel={() => {
              closeDialog?.('dialog-resource-move');
            }}
            saving={loadingMoveResources}
            disabled={!isTargetFolderSelectable}
          />
        </Group>
      </Stack>
    </Container>
  );
}

/* **************************** DIALOG INVOKATION *************************** */

export default DialogResourceMove;
